import { useState, useEffect, useRef } from 'react';
import { debug } from '@/utils/debug';
import { validateToken } from '@/utils/jwt';
import { SESSION_KEYS } from '@/config/constants';
import { useAuthStore } from '@/store/authStore';

// Helper to safely stringify unknown values for display
const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

// Mask values that look like JWT tokens or other bearer credentials so the
// debug panel never displays them verbatim, even in DEV builds running in a
// shared/staging environment.
const maskSensitive = (raw: string | null): string => {
  if (!raw) return '';
  if (/^eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(raw.trim())) {
    return `${raw.slice(0, 8)}…${raw.slice(-4)} (masked)`;
  }
  return raw;
};

interface APILog {
  id: number;
  timestamp: Date;
  method: string;
  url: string;
  status?: number;
  duration?: number;
  request?: unknown;
  response?: unknown;
  error?: unknown;
}

interface PerformanceMetric {
  name: string;
  value: number;
  unit: string;
}

export function DebugPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'api' | 'performance' | 'storage'>('overview');
  const [apiLogs, setApiLogs] = useState<APILog[]>([]);
  const [selectedLog, setSelectedLog] = useState<APILog | null>(null);
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetric[]>([]);
  const logIdCounter = useRef(0);
  const { user } = useAuthStore();

  // Keyboard shortcut: Ctrl+Shift+D
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setIsOpen(prev => {
          debug.log('DebugPanel: Toggled', !prev ? 'OPEN' : 'CLOSED');
          return !prev;
        });
      }
      // ESC to close
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Collect performance metrics
  useEffect(() => {
    if (!isOpen) return;

    const collectMetrics = () => {
      const metrics: PerformanceMetric[] = [];

      // Page load time
      if (performance.timing) {
        const loadTime = performance.timing.loadEventEnd - performance.timing.navigationStart;
        if (loadTime > 0) {
          metrics.push({ name: 'Page Load Time', value: loadTime, unit: 'ms' });
        }
      }

      // DOM Content Loaded
      if (performance.timing) {
        const domContentLoaded = performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart;
        if (domContentLoaded > 0) {
          metrics.push({ name: 'DOM Content Loaded', value: domContentLoaded, unit: 'ms' });
        }
      }

      // Memory (if available)
      const performanceWithMemory = performance as unknown as { memory?: { usedJSHeapSize?: number; jsHeapSizeLimit?: number } };
      if (performanceWithMemory.memory) {
        const memory = performanceWithMemory.memory;
        if (memory.usedJSHeapSize !== undefined) {
          metrics.push({
            name: 'JS Heap Size',
            value: Math.round(memory.usedJSHeapSize / 1024 / 1024),
            unit: 'MB'
          });
        }
        if (memory.jsHeapSizeLimit !== undefined) {
          metrics.push({
            name: 'JS Heap Limit',
            value: Math.round(memory.jsHeapSizeLimit / 1024 / 1024),
            unit: 'MB'
          });
        }
      }

      setPerformanceMetrics(metrics);
    };

    collectMetrics();
    const interval = setInterval(collectMetrics, 5000);
    return () => clearInterval(interval);
  }, [isOpen]);

  // Add API log
  const addApiLog = (log: Omit<APILog, 'id' | 'timestamp'>) => {
    setApiLogs(prev => {
      const newLog: APILog = {
        ...log,
        id: logIdCounter.current++,
        timestamp: new Date()
      };
      return [newLog, ...prev].slice(0, 100); // Keep last 100 logs
    });
  };

  // Expose API logger globally (in dev mode only)
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    (window as unknown as Record<string, unknown>).__debugPanel = {
      addApiLog,
      clearLogs: () => setApiLogs([]),
      logs: apiLogs
    };

    return () => {
      delete (window as unknown as Record<string, unknown>).__debugPanel;
    };
  }, [apiLogs]);

  if (!import.meta.env.DEV || import.meta.env.VITE_SHOW_DEBUG === 'false') {
    return null;
  }

  if (!isOpen) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsOpen(true)}
          className="bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-gray-700 transition-colors text-sm font-medium"
          title="Open Debug Panel (Ctrl+Shift+D)"
        >
          🔧 Debug
        </button>
      </div>
    );
  }

  const token = sessionStorage.getItem(SESSION_KEYS.AUTH_TOKEN);
  const tokenValidation = token ? validateToken(token) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end p-4 pointer-events-none">
      <div className="bg-white rounded-lg shadow-2xl border border-gray-300 w-full max-w-4xl h-[600px] flex flex-col pointer-events-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-2">
            <span className="text-lg">🔧</span>
            <h2 className="text-lg font-semibold text-gray-900">Debug Panel</h2>
            <span className="text-xs text-gray-500">(Ctrl+Shift+D to toggle)</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setApiLogs([]);
                setSelectedLog(null);
              }}
              className="text-sm px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
            >
              Clear Logs
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          {(['overview', 'api', 'performance', 'storage'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">🔐 Authentication Status</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">User:</span>
                    <span className="font-mono text-gray-900">{user?.email || 'Not logged in'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Member ID:</span>
                    <span className="font-mono text-gray-900">{user?.memberUuid || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Token Valid:</span>
                    <span className={`font-mono ${tokenValidation?.valid ? 'text-green-600' : 'text-red-600'}`}>
                      {tokenValidation?.valid ? '✓ Yes' : '✗ No'}
                    </span>
                  </div>
                  {tokenValidation?.valid && (
                    <div className="flex justify-between">
                      <span className="text-gray-700">Expires In:</span>
                      <span className="font-mono text-gray-900">
                        {Math.floor(tokenValidation.expiresIn / 60)} minutes
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-900 mb-2">📊 Quick Stats</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">API Logs:</span>
                    <span className="font-mono text-gray-900">{apiLogs.length} requests</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Session Storage Items:</span>
                    <span className="font-mono text-gray-900">{sessionStorage.length} items</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Environment:</span>
                    <span className="font-mono text-gray-900">
                      {import.meta.env.DEV ? 'Development' : 'Production'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="font-semibold text-purple-900 mb-2">⚡ Performance Metrics</h3>
                <div className="space-y-1 text-sm">
                  {performanceMetrics.map((metric, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-gray-700">{metric.name}:</span>
                      <span className="font-mono text-gray-900">
                        {metric.value} {metric.unit}
                      </span>
                    </div>
                  ))}
                  {performanceMetrics.length === 0 && (
                    <p className="text-gray-500 text-xs">Loading metrics...</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'api' && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 mb-4">
                {apiLogs.length > 0 ? (
                  <p>Showing {apiLogs.length} API request(s)</p>
                ) : (
                  <p className="text-center py-8">No API requests logged yet. Make some requests to see them here.</p>
                )}
              </div>
              <div className="space-y-2">
                {apiLogs.map(log => (
                  <div
                    key={log.id}
                    onClick={() => setSelectedLog(log)}
                    className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                      selectedLog?.id === log.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs font-mono px-2 py-1 rounded ${
                          log.method === 'GET' ? 'bg-green-100 text-green-800' :
                          log.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                          log.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                          log.method === 'DELETE' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {log.method}
                        </span>
                        <span className="text-sm font-mono text-gray-900">{log.url}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {log.status && (
                          <span className={`text-xs font-mono px-2 py-1 rounded ${
                            log.status >= 200 && log.status < 300 ? 'bg-green-100 text-green-800' :
                            log.status >= 400 ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {log.status}
                          </span>
                        )}
                        {log.duration && (
                          <span className="text-xs text-gray-500">{log.duration}ms</span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {log.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                ))}
              </div>

              {selectedLog && (
                <div className="mt-4 border-t pt-4">
                  <h4 className="font-semibold mb-2">Request Details</h4>
                  <div className="space-y-2">
                    {selectedLog.request !== undefined && selectedLog.request !== null && (
                      <div>
                        <p className="text-xs font-semibold text-gray-700 mb-1">Request:</p>
                        <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                          {safeStringify(selectedLog.request)}
                        </pre>
                      </div>
                    )}
                    {selectedLog.response !== undefined && selectedLog.response !== null && (
                      <div>
                        <p className="text-xs font-semibold text-gray-700 mb-1">Response:</p>
                        <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                          {safeStringify(selectedLog.response)}
                        </pre>
                      </div>
                    )}
                    {selectedLog.error !== undefined && selectedLog.error !== null && (
                      <div>
                        <p className="text-xs font-semibold text-red-700 mb-1">Error:</p>
                        <pre className="text-xs bg-red-50 p-2 rounded overflow-auto max-h-40 text-red-800">
                          {safeStringify(selectedLog.error)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'performance' && (
            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="font-semibold text-purple-900 mb-4">⚡ Performance Metrics</h3>
                <div className="space-y-3">
                  {performanceMetrics.map((metric, i) => (
                    <div key={i} className="border-b border-purple-100 pb-2">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-900">{metric.name}</span>
                        <span className="text-lg font-mono font-semibold text-purple-600">
                          {metric.value} {metric.unit}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">📈 API Performance</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Total Requests:</span>
                    <span className="font-mono text-gray-900">{apiLogs.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Average Response Time:</span>
                    <span className="font-mono text-gray-900">
                      {apiLogs.length > 0
                        ? Math.round(apiLogs.reduce((sum, log) => sum + (log.duration || 0), 0) / apiLogs.length)
                        : 0}ms
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Failed Requests:</span>
                    <span className="font-mono text-gray-900">
                      {apiLogs.filter(log => log.error || (log.status && log.status >= 400)).length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'storage' && (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="font-semibold text-yellow-900 mb-2">💾 Session Storage</h3>
                <div className="space-y-2">
                  {Object.keys(sessionStorage).map((key, i) => (
                    <div key={i} className="border-b border-yellow-100 pb-2">
                      <div className="flex justify-between items-start">
                        <span className="text-sm font-mono text-gray-700 break-all">{key}</span>
                        <button
                          onClick={() => {
                            sessionStorage.removeItem(key);
                            // Force re-render
                            setActiveTab('overview');
                            setTimeout(() => setActiveTab('storage'), 10);
                          }}
                          className="text-xs text-red-600 hover:text-red-800 ml-2"
                        >
                          Remove
                        </button>
                      </div>
                      <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-auto max-h-20">
                        {maskSensitive(sessionStorage.getItem(key))}
                      </pre>
                    </div>
                  ))}
                  {sessionStorage.length === 0 && (
                    <p className="text-sm text-gray-500">No session storage items</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
